import { logger } from '@/engine/logging';
import { ParticleSystem } from './ParticleSystem';
import { AudioManager } from '@/engine/audio/AudioManager';
import * as PIXI from 'pixi.js';
import { effectPresets, type EffectPresetName } from './effect-presets';
import { ServiceRegistry, ServiceKeys } from '@/engine/registry';
import { LayerManager, LAYER_DEPTHS } from '@/engine/render/LayerManager';
import type { ParticleProperties } from './ParticleProperties';

export class EffectSystem {
  requiredComponents = [];
  private particleSystem!: ParticleSystem;
  private backgroundParticleSystem!: ParticleSystem; // Система для фоновых частиц
  private screenShakeIntensity: number = 0;
  private screenShakeDuration: number = 0;
  private camera: PIXI.Container;
  private particleContainer!: PIXI.Container; // Храним контейнер частиц отдельно
  private backgroundParticleContainer!: PIXI.Container; // Контейнер для фоновых частиц
  private baseCameraPosition: { x: number; y: number } = { x: 0, y: 0 };
  private currentShakeOffset: { x: number; y: number } = { x: 0, y: 0 };
  private lastShakeTime: number = 0;
  private minShakeInterval: number = 0.05; // Минимальный интервал между трясками (50ms)
  private audioManager: AudioManager | null = null;
  private layerManager: LayerManager | null = null;
  private layerParticleSystems = new Map<number, ParticleSystem>(); // Кэш систем частиц для разных слоев

  constructor(
    stage: PIXI.Container,
    renderer?: PIXI.Renderer,
    audioManager?: AudioManager,
    layerManager?: LayerManager
  ) {
    this.camera = stage;
    this.audioManager = audioManager || null;
    this.layerManager = layerManager || null;
    this.initializeParticleSystem(stage, renderer);
  }

  /**
   * Устанавливает новый контейнер для эффектов
   */
  setContainer(newContainer: PIXI.Container, layerManager?: LayerManager): void {
    this.destroyParticleSystem(true);
    this.camera = newContainer;
    this.layerManager = layerManager || null;
    this.baseCameraPosition = { x: newContainer.x, y: newContainer.y };
    this.initializeParticleSystem(newContainer);
    logger.info('EffectSystem container updated', { source: 'effects' });
  }

  getContainer(): PIXI.Container {
    return this.particleContainer;
  }

  getBackgroundContainer(): PIXI.Container {
    return this.backgroundParticleContainer;
  }

  /**
   * Устанавливает AudioManager для воспроизведения звуков
   */
  setAudioManager(audioManager: AudioManager): void {
    this.audioManager = audioManager;
    logger.info('🔊 AudioManager установлен в EffectSystem', { source: 'effects' });
  }

  update(deltaTime: number): void {
    // Важно: обновление шейка до обновления камеры, если есть логика слежения
    this.updateScreenShake(deltaTime);
    this.particleSystem.update(deltaTime);
    this.backgroundParticleSystem.update(deltaTime);

    // Обновляем все системы частиц для слоев
    this.layerParticleSystems.forEach(system => system.update(deltaTime));
  }

  // Основной метод для запуска эффектов
  public triggerEffect(
    name: String,
    x: number,
    y: number,
    customColor?: number,
    layer?: number,
    useBackground?: boolean
  ): void {
    const preset = effectPresets[name as EffectPresetName] as any; // Type assertion to avoid complex union types
    if (!preset) {
      logger.warn(`Effect preset "${name}" not found.`, { source: 'effects' });
      return;
    }

    logger.info(`🎆 Triggering effect "${name}" at (${x}, ${y})`, { source: 'effects' });

    // Emit particles using new ParticleProperties system
    for (const particle of preset.particles) {
      const properties = { ...particle.properties };

      // Override color if provided
      if (customColor !== undefined) {
        properties.color = {
          start: customColor,
          end: customColor,
          ease: properties.color?.ease || ((t: number) => t),
        };
      }

      // Emit to specific layer or default
      if (layer !== undefined && this.layerManager) {
        this.emitToLayer(properties, x, y, layer);
      } else if (useBackground) {
        this.backgroundParticleSystem.emit(properties, x, y);
      } else {
        this.particleSystem.emit(properties, x, y);
      }
    }

    // Play sound
    if (preset.sound && this.audioManager) {
      this.audioManager.playSFX(preset.sound as any);
    } else if (preset.sound && !this.audioManager) {
      logger.warn('🔇 AudioManager not set, sound not played', {
        sound: preset.sound,
        source: 'effects',
      });
    }

    // Trigger screen shake
    if (preset.screenShake) {
      this.triggerScreenShake(preset.screenShake.intensity, preset.screenShake.duration);
    }
  }

  // Screen effects
  triggerScreenShake(intensity: number, duration: number): void {
    const currentTime = performance.now() / 1000; // Время в секундах

    // Проверяем кулдаун для слабых трясок (майнинг, искры)
    if (intensity < 1.0 && currentTime - this.lastShakeTime < this.minShakeInterval) {
      return; // Пропускаем слабую тряску, если прошло мало времени
    }

    this.screenShakeIntensity = Math.max(this.screenShakeIntensity, intensity);
    this.screenShakeDuration = Math.max(this.screenShakeDuration, duration);
    this.lastShakeTime = currentTime;
  }

  private updateScreenShake(deltaTime: number): void {
    if (this.screenShakeDuration > 0) {
      // Убираем предыдущее смещение
      this.camera.x -= this.currentShakeOffset.x;
      this.camera.y -= this.currentShakeOffset.y;

      // Вычисляем новое смещение
      this.currentShakeOffset.x = (Math.random() - 0.5) * this.screenShakeIntensity * 10;
      this.currentShakeOffset.y = (Math.random() - 0.5) * this.screenShakeIntensity * 10;

      // Применяем новое смещение
      this.camera.x += this.currentShakeOffset.x;
      this.camera.y += this.currentShakeOffset.y;

      this.screenShakeDuration -= deltaTime;
      this.screenShakeIntensity *= 0.9; // Decay

      if (this.screenShakeDuration <= 0) {
        // Убираем последнее смещение
        this.camera.x -= this.currentShakeOffset.x;
        this.camera.y -= this.currentShakeOffset.y;
        this.currentShakeOffset = { x: 0, y: 0 };
        this.screenShakeIntensity = 0;
      }
    }
  }

  async playUISound(soundType: string): Promise<void> {
    if (!this.audioManager) {
      logger.warn('🔇 AudioManager не установлен, UI звук не воспроизведен', {
        soundType,
        source: 'effects',
      });
      return;
    }

    switch (soundType) {
      case 'click':
        await this.audioManager.playSFX('ui_click');
        break;
      case 'hover':
        await this.audioManager.playSFX('ui_hover');
        break;
      case 'purchase':
        await this.audioManager.playSFX('ui_purchase');
        break;
      case 'error':
        await this.audioManager.playSFX('ui_error');
        break;
      case 'notification':
        await this.audioManager.playSFX('ui_notification');
        break;
    }
  }

  // Camera effects
  triggerZoom(targetScale: number, _duration: number = 0.5): void {
    // This would be implemented with a tween system
    // For now, just set the scale directly
    this.camera.scale.set(targetScale);
  }

  resetCamera(): void {
    this.camera.x = this.baseCameraPosition.x;
    this.camera.y = this.baseCameraPosition.y;
    this.camera.scale.set(1);
  }

  // Utility methods
  start(): void {
    logger.info('[EffectSystem] start() called', { source: 'game' });
    this.particleSystem.start();
    this.backgroundParticleSystem.start();
  }

  stop(): void {
    this.particleSystem.stop();
    this.backgroundParticleSystem.stop();
    this.resetCamera();
  }

  clear(): void {
    this.particleSystem.clear();
    this.backgroundParticleSystem.clear();
  }

  destroy(): void {
    this.stop();
    this.destroyParticleSystem(true);

    // Очищаем все системы частиц для слоев
    this.layerParticleSystems.forEach(system => system.destroy());
    this.layerParticleSystems.clear();

    this.audioManager = null;
    this.layerManager = null;
    logger.info('🧹 EffectSystem destroyed', { source: 'effects' });
  }

  getParticleCount(): number {
    return (
      this.particleSystem.getParticleCount() + this.backgroundParticleSystem.getParticleCount()
    );
  }

  getBackgroundParticleCount(): number {
    return this.backgroundParticleSystem.getParticleCount();
  }

  private emitToLayer(properties: ParticleProperties, x: number, y: number, layer: number): void {
    if (!this.layerManager) {
      // Fallback к основной системе частиц
      this.particleSystem.emit(properties, x, y);
      return;
    }

    // Получаем или создаем систему частиц для этого слоя
    let layerParticleSystem = this.layerParticleSystems.get(layer);
    if (!layerParticleSystem) {
      const layerContainer = new PIXI.Container();
      this.layerManager.addToLayer(layer, layerContainer);

      const pixiRenderer = this.resolveRenderer();
      layerParticleSystem = new ParticleSystem(layerContainer, pixiRenderer, 1000);
      this.layerParticleSystems.set(layer, layerParticleSystem);
    }

    layerParticleSystem.emit(properties, x, y);
  }

  /**
   * Перезагрузка шейдеров для HMR
   * Проходит по всем спрайтам в контейнере и пересоздает фильтры
   */
  reloadShaders(): void {
    logger.info('♻️ Reloading shaders for HMR...', { source: 'effects' });

    let reloadedCount = 0;

    // Рекурсивно обходим все контейнеры и спрайты
    const processContainer = (container: PIXI.Container) => {
      for (const child of container.children) {
        if (child instanceof PIXI.Sprite && child.filters) {
          // Пересоздаем фильтры для каждого спрайта
          const newFilters: PIXI.Filter[] = [];

          for (const filter of child.filters) {
            try {
              // HMR: если у фильтра есть метод getReloadParams, пересоздаём его
              if (
                'getReloadParams' in filter &&
                typeof (filter as any).getReloadParams === 'function'
              ) {
                const params = (filter as any).getReloadParams();
                const NewFilter = filter.constructor as any;
                newFilters.push(new NewFilter(params));
                reloadedCount++;
              } else {
                // Для остальных фильтров (встроенных, etc) — оставляем как есть
                newFilters.push(filter);
              }
            } catch (error) {
              logger.warn('⚠️ Failed to reload filter', {
                filterType: filter.constructor.name,
                error: error instanceof Error ? error.message : String(error),
                source: 'effects',
              });
              // Оставляем старый фильтр в случае ошибки
              newFilters.push(filter);
            }
          }

          // Применяем новые фильтры
          child.filters = newFilters;
        }

        // Рекурсивно обрабатываем дочерние контейнеры
        if (child instanceof PIXI.Container) {
          processContainer(child);
        }
      }
    };

    // Обрабатываем основной контейнер
    processContainer(this.camera);

    logger.info(`✅ Reloaded ${reloadedCount} shaders`, { source: 'effects' });
  }

  private resolveRenderer(explicit?: PIXI.Renderer): PIXI.Renderer {
    if (explicit) {
      return explicit;
    }

    if (ServiceRegistry.has(ServiceKeys.PixiApp)) {
      const app = ServiceRegistry.get<PIXI.Application>(ServiceKeys.PixiApp);
      return app.renderer;
    }

    throw new Error('PIXI Renderer not available. Ensure PixiRenderer initialized first.');
  }

  private initializeParticleSystem(container: PIXI.Container, renderer?: PIXI.Renderer): void {
    // Создаем контейнер для обычных частиц
    this.particleContainer = new PIXI.Container();

    // Создаем контейнер для фоновых частиц
    this.backgroundParticleContainer = new PIXI.Container();

    if (this.layerManager) {
      // Фоновые частицы на более низком слое
      this.layerManager.addToLayer(
        LAYER_DEPTHS.BACKGROUND_PARTICLES,
        this.backgroundParticleContainer
      );
      this.layerManager.addToLayer(LAYER_DEPTHS.PARTICLES, this.particleContainer);
    } else {
      // Без LayerManager - добавляем в правильном порядке
      container.addChild(this.backgroundParticleContainer);
      container.addChild(this.particleContainer);
    }

    const pixiRenderer = this.resolveRenderer(renderer);

    // Создаем системы частиц
    this.particleSystem = new ParticleSystem(this.particleContainer, pixiRenderer, 2000);
    this.backgroundParticleSystem = new ParticleSystem(
      this.backgroundParticleContainer,
      pixiRenderer,
      1000
    );

    // Запускаем системы частиц
    this.particleSystem.start();
    this.backgroundParticleSystem.start();

    this.baseCameraPosition = { x: container.x, y: container.y };
  }

  private destroyParticleSystem(removeContainer: boolean): void {
    if (this.particleSystem) {
      this.particleSystem.destroy();
    }

    if (this.backgroundParticleSystem) {
      this.backgroundParticleSystem.destroy();
    }

    if (removeContainer) {
      if (this.particleContainer?.parent) {
        this.particleContainer.removeChildren();
        this.particleContainer.parent.removeChild(this.particleContainer);
        this.particleContainer.destroy();
      }

      if (this.backgroundParticleContainer?.parent) {
        this.backgroundParticleContainer.removeChildren();
        this.backgroundParticleContainer.parent.removeChild(this.backgroundParticleContainer);
        this.backgroundParticleContainer.destroy();
      }
    }
  }
}
